import { useState, createContext, useContext, useMemo, useEffect } from 'react'

import { ThemeProvider } from 'styled-components'

import { envStorage } from '../stores/env'

import darkTheme from './darkTheme'
import lightTheme from './lightTheme'
import useDetectColorScheme from './useDetectColorScheme'

// todo: add 'system' which would always listen to the system value

type DarkModeContextType = {
  darkMode: string
  setDarkMode: (key: string) => void
  sysDarkMode: string | null
}

const DarkModeContext = createContext<DarkModeContextType>({
  darkMode: 'light',
  setDarkMode: () => {},
  sysDarkMode: 'light',
})

const DarkModeProvider = ({ children }) => {
  const sysDarkMode = useDetectColorScheme()

  const [darkMode, setDarkMode] = useState(
    () => envStorage.get('darkMode') || sysDarkMode,
  )

  useEffect(() => {
    if (!envStorage.get('darkMode') && sysDarkMode) {
      setDarkMode(sysDarkMode)
    }
  }, [sysDarkMode])

  const storeDarkMode = useMemo(
    () => (key: string) => {
      setDarkMode(key)
      envStorage.set('darkMode', key)
    },
    [],
  )

  const darkModeInfo = useMemo(
    () => ({
      darkMode,
      setDarkMode: storeDarkMode,
      sysDarkMode,
    }),
    [darkMode, storeDarkMode, sysDarkMode],
  )

  const theme = darkMode === 'dark' ? darkTheme : lightTheme

  return (
    <DarkModeContext.Provider value={darkModeInfo}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </DarkModeContext.Provider>
  )
}

const useDarkModeInfo = () => useContext(DarkModeContext)

export { useDarkModeInfo }

export default DarkModeProvider
